import React from "react";
import { submitFeedback } from "./boot.js";
import * as UI from "./ui.jsx";

// Feedback button — a pill in the bottom-right corner on a desktop, and the
// dialog behind it. Following the Repository project's FeedbackWidget, which is
// the same button on the same identity provider. On a phone or tablet the pill
// sat over the page and the tab bar (reported 24 September 2026), so there the
// App draws no pill and the shell's menu opens the dialog; the App owns `open`
// so either way in reaches the same dialog.
//
// The page sends a description and nothing else that matters: the host attaches
// the identity it verified for the request and files the GitHub issue with its
// own credential. Nothing here can choose who the report comes from.

const { useState, useRef, useEffect } = React;

const LIMIT = 5000;

function FeedbackWidget({ open, onOpenChange, pill = true }) {
  const setOpen = onOpenChange;
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [issue, setIssue] = useState(null);
  const dialogRef = useRef(null);
  const textRef = useRef(null);

  useEffect(() => { if (open) textRef.current?.focus(); }, [open]);

  function reset() { setText(""); setError(""); setIssue(null); }
  function close() { setOpen(false); reset(); }

  async function send() {
    const description = text.trim();
    if (!description) { setError("Describe what happened before sending."); return; }
    setBusy(true);
    setError("");
    try {
      setIssue(await submitFeedback(description));
      setText("");
    } catch (failure) {
      const kind = String(failure && failure.message);
      if (kind === "feedback_unavailable") {
        setError("Feedback is not configured in this environment yet. Nothing was sent.");
      } else if (kind === "feedback_rate_limited") {
        setError("Too many reports just went through. Wait a minute and try again.");
      } else if (kind === "description_too_long") {
        setError(`That is longer than ${LIMIT} characters. Shorten it and send again.`);
      } else if (kind === "access_not_provisioned") {
        setError("This account cannot file reports.");
      } else {
        setError("The report could not be sent. Nothing was filed — try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  function keepFocus(event) {
    if (event.key === "Escape" && !busy) { event.preventDefault(); close(); return; }
    if (event.key !== "Tab") return;
    const focusable = [...(dialogRef.current?.querySelectorAll(
      "button:not(:disabled), textarea:not(:disabled), a[href]") || [])];
    if (!focusable.length) { event.preventDefault(); dialogRef.current?.focus(); return; }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  if (!open) {
    if (!pill) return null;
    return <button type="button" className="feedback-pill" onClick={() => setOpen(true)}
      aria-label="Send feedback">
      <UI.Icon.mail style={{ width: 16, height: 16 }}/><span>Feedback</span>
    </button>;
  }

  return <div className="feedback-wrap" role="presentation" onClick={() => { if (!busy) close(); }}>
    <section ref={dialogRef} className="feedback-dialog" role="dialog" aria-modal="true"
      tabIndex="-1" onKeyDown={keepFocus} onClick={event => event.stopPropagation()}
      aria-labelledby="feedback-title">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <span className="eyebrow">Vendor Dashboard</span>
          <h2 id="feedback-title">Send feedback</h2>
        </div>
        <button type="button" className="feedback-close" onClick={close} disabled={busy}
          aria-label="Close">
          <UI.Icon.close style={{ width: 20, height: 20 }}/>
        </button>
      </div>

      {issue ? <>
        <p>
          Thanks — filed as{" "}
          <a href={issue.issueUrl} target="_blank" rel="noopener noreferrer">issue #{issue.issueNumber}</a>.
        </p>
        <div className="feedback-actions">
          <button type="button" className="btn primary" onClick={close}>Done</button>
          <button type="button" className="btn" onClick={reset}>Report something else</button>
        </div>
      </> : <>
        <p>
          What is broken, confusing or missing? The page you are on and who you are
          signed in as are attached automatically.
        </p>
        {error && <p className="error-text" role="alert">{error}</p>}
        <div className="field">
          <label htmlFor="feedback-text">Your report</label>
          <textarea id="feedback-text" ref={textRef} value={text} rows={7} maxLength={LIMIT}
            disabled={busy} placeholder="Describe what happened, and what you expected instead."
            onChange={event => setText(event.target.value)}/>
        </div>
        <div className="feedback-actions">
          <button type="button" className="btn primary" disabled={busy || !text.trim()} onClick={send}>
            {busy ? "Sending…" : "Send report"}
          </button>
          <button type="button" className="btn" disabled={busy} onClick={close}>Cancel</button>
          <span className="caption feedback-count">{text.length} / {LIMIT}</span>
        </div>
      </>}
    </section>
  </div>;
}

export { FeedbackWidget };
